import { createContext, useContext, ReactNode } from 'react';
import type { Id, SavedLocation } from '@hero/schema';
import {
  MapLocationData,
  MarkerConfig,
  MapMode,
  LocationMode,
  SavePreparingReason,
} from '@hero/web-components';
import type { GoogleMapWithMarkerProps } from '@hero/web-components';

/** Props passed to map (no API callables; components use api-hooks at use site). */
export type MapContextProps = GoogleMapWithMarkerProps;

/**
 * Map context value containing all state, handlers, and utilities for map components
 */
export interface MapContextValue {
  // ========== STATE ==========
  /** Google Maps instance */
  map: google.maps.Map | null;
  /** Array of all locations on the map */
  locations: MapLocationData[];
  /** Index of location currently being edited (null if not editing) */
  editingIndex: number | null;
  /** User's current location */
  currentLocation: MapLocationData | null;
  /** Whether Google Maps API is loaded */
  isLoaded: boolean;
  /** Error from loading Google Maps API */
  loadError: Error | null;
  /** Whether current location is being fetched */
  isGettingCurrentLocation: boolean;

  // ========== CONFIG ==========
  /** Current map mode: view, edit, or add_new_location */
  mode: MapMode;
  /** Location mode: single or multi */
  locationMode: LocationMode;
  /** Whether to show user's current location marker */
  showUserCurrentLocation: boolean;
  /** Route line type: actual_route or straight */
  routeLineType: 'straight' | 'actual_route';
  /** Marker icon type */
  markerIcon: 'normal_location_icon' | 'order_pickup_dropoff_icons';
  /** Whether to show confirm locations button */
  enableConfirmLocationsButton: boolean;
  /** When true, Save Changes button shows loading state (same as form modal). */
  saveInProgress?: boolean;
  /** When true, Save is disabled while pre-save work (e.g. a price quote) is in flight. */
  savePreparing?: boolean;
  /** Names that work, so Save can say "Checking price..." rather than "Loading...". */
  savePreparingReason?: SavePreparingReason;
  /** Whether to show fit all button */
  showFitAllButton: boolean;
  /** Whether to show accurate location indicator (red pulse + red stroke) for inaccurate locations */
  enableAccurateLocationIndicator: boolean;

  // ========== COMPUTED ==========
  /** Whether to show search component */
  showSearch: boolean;
  /** Marker configurations for all locations */
  markers: MarkerConfig[];
  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean;
  /** Check if a specific location has been edited */
  isLocationEdited: (index: number) => boolean;
  /** Update confirmed locations snapshot */
  updateConfirmedLocations: (locations: MapLocationData[]) => void;
  /** Location currently being edited */
  editingLocation: MapLocationData | null;

  // ========== HANDLERS ==========
  /** Handle map load - set the map instance */
  onMapLoad: (mapInstance: google.maps.Map) => void;
  /** Handle map click event */
  handleMapClick: (event: google.maps.MapMouseEvent) => Promise<void>;
  /** Handle location selection from search */
  handleLocationSelect: (location: MapLocationData, index: number) => void;
  /** Handle marker/card click - zoom to location */
  handleCardClick: (index: number) => void;
  /** Handle location edit start */
  handleLocationEdit: (index: number, isOrigin: boolean) => void | Promise<void>;
  /** Handle location edit cancel */
  handleLocationCancel: (index: number, isOrigin: boolean) => void;
  /** Handle location edit done */
  handleLocationDone: (index: number, isOrigin: boolean) => void;
  /**
   * Handle accurate location toggle. `patch` carries a better pin that
   * arrived with the decision (e.g. resolved from a pasted Google Maps
   * link), so both land in a single state update.
   */
  handleAccurateLocationToggle: (
    index: number,
    isOrigin: boolean,
    isAccurate: boolean,
    patch?: Partial<MapLocationData>,
  ) => void;
  /** Handle save changes button */
  handleSaveChanges: () => void;
  /** Handle cancel changes button */
  handleCancel: () => void;
  /** Handle pending marker drag (when editing) */
  handlePendingMarkerDrag: (lat: number, lng: number) => Promise<void>;
  /** Handle save location request (from LocationCard). Optional index ensures the correct slot is updated after creating a new location. */
  handleSaveLocationRequested?: (
    coordinates: { lat: number; lng: number },
    index?: number | null,
  ) => void;

  // ========== MAP UTILITIES ==========
  /** Fit bounds to show all locations */
  fitBounds: (map: google.maps.Map, locations: MapLocationData[]) => void;
  /** Smoothly zoom to a location */
  smoothZoomToLocation: (
    map: google.maps.Map,
    position: google.maps.LatLngLiteral,
    zoom?: number,
  ) => void;

  // ========== DIRECTIONS ==========
  /** Calculated directions results */
  directions: google.maps.DirectionsResult[];
  /** Key for forcing DirectionsRenderer remount */
  directionsKey: number;
  /** Whether directions are being calculated */
  isCalculating: boolean;
  /** Clear all directions immediately */
  clearDirections: () => void;
  /** Recalculate directions based on current locations */
  recalculateDirections: () => void;

  // ========== REFS ==========
  /** Ref to MapSearch component for address fetching */
  mapSearchRef: React.RefObject<{
    handleCoordinates: (lat: number, lng: number, index: number) => Promise<void>;
    isClosingDropdownRef: React.MutableRefObject<boolean>;
  }>;

  // ========== CALLBACKS ==========
  /** Callback to update current location */
  onCurrentLocationChange?: (location: MapLocationData | null) => void;

  // ========== SAVE LOCATION ==========
  /** Save location state and handlers (no API callables; LocationEditModal uses api-hooks internally). */
  saveLocationState?: {
    isLocationModalOpen: boolean;
    locationModalCoords?: { lat: number; lng: number };
    locationModalIndex: number | null;
    locationModalSavedLocationId?: Id;
    /**
     * Maps link already on the stop being saved as a place, pre-filled into
     * the form so the provenance isn't asked for twice.
     */
    locationModalLocationUrl?: string;
    handleSaveLocationRequested: (
      coordinates: { lat: number; lng: number },
      index?: number | null,
    ) => void;
    /** Open the saved location form in edit mode for the given saved location and card index. */
    handleEditSavedLocationRequested?: (savedLocationId: Id, index: number) => void;
    handleLocationSaved: (savedLocation: SavedLocation) => void;
    closeLocationModal: () => void;
    orgId?: string;
    isSaveLocationAvailable: boolean;
  };

  // ========== PROPS ==========
  /** Original component props, augmented with derived APIs (for sub-components that need them) */
  props: MapContextProps;
}

const MapContext = createContext<MapContextValue | undefined>(undefined);

/**
 * Provider component for MapContext
 * Wraps map components and provides context value
 */
export function MapProvider({ value, children }: { value: MapContextValue; children: ReactNode }) {
  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

/**
 * Hook to access MapContext
 * Must be used within a MapProvider
 *
 * @throws Error if used outside MapProvider
 */
export function useMapContext() {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error('useMapContext must be used within a MapProvider');
  }
  return context;
}
